import type { PublicReceiptEntity, PublicReceiptSession } from '../../domain/entities/PublicReceiptEntity.js';
import type { IPublicReceiptRepository } from '../../domain/repositories/IPublicReceiptRepository.js';
import { capsToMoney, serviceFee } from '../../domain/prizeRates.js';
import { PublicReceiptRemoteDataSource } from '../datasources/PublicReceiptRemoteDataSource.js';
import type { PublicReceiptDto } from '../dtos/PublicReceiptDto.js';

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0));
  return isNaN(n) ? 0 : n;
}

export class PublicReceiptRepository implements IPublicReceiptRepository {
  constructor(private readonly ds: PublicReceiptRemoteDataSource) {}

  async getByCode(code: string): Promise<PublicReceiptEntity | null> {
    const dto = await this.ds.getByCode(code);
    if (!dto) return null;
    return this.toEntity(dto);
  }

  private toEntity(dto: PublicReceiptDto): PublicReceiptEntity {
    const sessions: PublicReceiptSession[] = (dto.sessions ?? []).map((s) => {
      const prize1 = num(s.prize1), prize2 = num(s.prize2), prize3 = num(s.prize3);
      return {
        date: s.date,
        prize1,
        prize2,
        prize3,
        totalCaps: prize1 + prize2 + prize3,
        money: capsToMoney({ prize1, prize2, prize3 }),
      };
    });

    const caps = {
      prize1: num(dto.caps?.prize1),
      prize2: num(dto.caps?.prize2),
      prize3: num(dto.caps?.prize3),
      totalCaps: num(dto.caps?.totalCaps),
    };

    return {
      location: {
        name: dto.location.name,
        code: dto.location.code,
        addressLine: dto.location.addressLine,
        provinceName: dto.location.provinceName ?? null,
      },
      exchangeCount: num(dto.exchangeCount),
      caps,
      totalMoney: capsToMoney(caps),
      serviceFee: serviceFee(capsToMoney(caps)),
      sessions,
    };
  }
}
